import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // fix-209: media → variant reverse sync. When a variant-specific image is viewed in the
    // zoom dialog, select its variant on dialog close (deferred so the open lightbox isn't torn
    // down by the variant-driven gallery rebuild). Works on mobile/tablet/desktop alike.
    this.#parseVariantMediaMap();
    if (this.#variantRows) {
      window.addEventListener(DialogCloseEvent.eventName, this.#handleZoomDialogClose, { signal });
      // Capture-phase scroll: catches the dialog's vertical scroll (desktop) AND the inner
      // gallery's horizontal swipe (mobile) — Horizon's own zoom-media:selected only fires for
      // the desktop dialog scroll, so this is what makes mobile/tablet work.
      this.refs.zoomDialogComponent?.addEventListener('scroll', this.#handleZoomScroll, {
        signal,
        capture: true,
        passive: true,
      });
    }
  }

  #controller = new AbortController();

  /** @type {{ m: number, o: string[] }[] | null} */
  #variantRows = null;

  /** @type {number | null} */
  #lastZoomMediaId = null;

  /** @type {number} */
  #zoomScrollRaf = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });

    // fix-209: track which media is currently in view so the variant can be synced on close.
    if (this.#variantRows) {
      const li = this.refs.zoomDialogComponent?.refs.media?.[event.detail.index];
      const mediaId = li instanceof HTMLElement ? Number(li.dataset.mediaId) : NaN;
      this.#lastZoomMediaId = Number.isNaN(mediaId) ? null : mediaId;
    }
  };

  /**
   * fix-209: when the zoom dialog closes, select the variant tied to the last-viewed image.
   */
  #handleZoomDialogClose = () => {
    const mediaId = this.#lastZoomMediaId;
    this.#lastZoomMediaId = null;
    if (mediaId) this.#selectVariantForMedia(mediaId);
  };

  /**
   * fix-209: throttled scroll handler that records the media centered in the zoom viewport.
   */
  #handleZoomScroll = () => {
    if (this.#zoomScrollRaf) return;
    this.#zoomScrollRaf = requestAnimationFrame(() => {
      this.#zoomScrollRaf = 0;
      this.#trackCenteredZoomMedia();
    });
  };

  /**
   * Records the zoom media currently under the viewport centre (axis-agnostic, so it works for
   * the desktop vertical stack and the mobile horizontal carousel).
   */
  #trackCenteredZoomMedia() {
    const media = this.refs.zoomDialogComponent?.refs.media;
    if (!media?.length) return;

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;

    for (const li of media) {
      if (!(li instanceof HTMLElement)) continue;
      const rect = li.getBoundingClientRect();
      if (rect.left <= cx && rect.right >= cx && rect.top <= cy && rect.bottom >= cy) {
        const id = Number(li.dataset.mediaId);
        if (!Number.isNaN(id)) this.#lastZoomMediaId = id;
        return;
      }
    }
  }

  /**
   * Parses the compact variant→media map emitted alongside the gallery.
   */
  #parseVariantMediaMap() {
    const script = this.querySelector('[data-variant-media-map]');
    if (!script) return;

    try {
      const rows = JSON.parse(script.textContent || '[]');
      const filtered = Array.isArray(rows) ? rows.filter((row) => row && row.m && Array.isArray(row.o)) : [];
      this.#variantRows = filtered.length ? filtered : null;
    } catch {
      this.#variantRows = null;
    }
  }

  /**
   * Selects the variant whose featured media matches the given media id. Only options that the
   * image unambiguously determines are changed (e.g. a "Red" image shared by Red/S, Red/M, Red/L
   * sets Color=Red and leaves Size as-is).
   * @param {number} mediaId
   */
  #selectVariantForMedia(mediaId) {
    const rows = this.#variantRows?.filter((row) => row.m === mediaId);
    if (!rows || !rows.length) return;

    const picker = this.#variantPicker;
    if (!picker) return;

    const optionCount = rows[0].o.length;
    /** @type {HTMLElement | null} */
    let lastChanged = null;

    for (let i = 0; i < optionCount; i++) {
      const values = new Set(rows.map((row) => row.o[i]));
      if (values.size !== 1) continue; // image spans multiple values for this option → leave as-is
      const changed = this.#setPickerOption(picker, i, rows[0].o[i]);
      if (changed) lastChanged = changed;
    }

    // A single change event is enough: variant-picker reads all currently-checked inputs.
    lastChanged?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Sets one option in the variant picker by option index + value (supports button/swatch radios
   * and dropdown selects). Returns the control if it actually changed, otherwise null.
   * @param {Element} picker
   * @param {number} optionIndex
   * @param {string} value
   * @returns {HTMLElement | null}
   */
  #setPickerOption(picker, optionIndex, value) {
    // Button / swatch style: radios carry data-fieldset-index = true option index.
    const radios = picker.querySelectorAll(`input[type="radio"][data-fieldset-index="${optionIndex}"]`);
    if (radios.length) {
      const match = Array.from(radios).find(
        (radio) => radio instanceof HTMLInputElement && radio.value === value
      );
      if (!(match instanceof HTMLInputElement) || match.checked) return null;
      match.checked = true;
      return match;
    }

    // Dropdown style: options carry data-input-id="<1-based position>-<value index>".
    for (const select of picker.querySelectorAll('select')) {
      const firstOption = select.querySelector('option[data-input-id]');
      if (!(firstOption instanceof HTMLOptionElement)) continue;
      if (!firstOption.dataset.inputId?.startsWith(`${optionIndex + 1}-`)) continue;
      if (select.value === value) return null;
      if (!Array.from(select.options).some((option) => option.value === value)) return null;
      select.value = value;
      return select;
    }

    return null;
  }

  /**
   * The main-product variant picker in this section (if any).
   * @returns {Element | null}
   */
  get #variantPicker() {
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector('variant-picker[data-template-product-match="true"]') ??
      section?.querySelector('variant-picker') ??
      null
    );
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
